import cx from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './PreviewImage.module.scss';
import type { PreviewImageType } from './PreviewImages';
import FontAwesomeIcon, {
  lightAngleRight
} from 'common/components/FontAwesomeIcon';

type Props = { image?: PreviewImageType };

type ButtonProps = { handleClick: () => void };

function defaultPreventer(
  callback: () => void
): (e: MouseEvent<HTMLButtonElement>) => void {
  return e => {
    e.preventDefault();
    callback();
  };
}

export function NextButton(props: ButtonProps): ReactNode {
  return (
    <button
      className={styles.nextButton}
      onClick={defaultPreventer(props.handleClick)}
      aria-label={__('next')}
    >
      <FontAwesomeIcon icon={lightAngleRight} />
    </button>
  );
}

export function PreviousButton(props: ButtonProps): ReactNode {
  return (
    <button
      className={styles.prevButton}
      onClick={defaultPreventer(props.handleClick)}
      aria-label={__('previous')}
    >
      <FontAwesomeIcon icon={lightAngleRight} />
    </button>
  );
}

export function PreviewImage(props: Props): ReactNode {
  const [loading, setLoading] = useState(true);
  const imageNode = useRef<HTMLImageElement | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { image } = props;

  const imageLoaded = useCallback(() => {
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    setLoading(false);
  }, []);

  useEffect(() => {
    // Reset loading state when image changes
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      setLoading(true);
    }, 500);

    // Check if image is already loaded on mount
    if (imageNode.current && imageNode.current.complete) {
      imageLoaded();
    }

    return () => {
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, [image, imageLoaded]);

  const { url: imageUrl, placeholder } = image || {};
  const renderImage = !!imageUrl;
  const backgroundImage = placeholder ? `url('${placeholder}')` : undefined;

  return (
    <div
      className={styles.previewImageWrapper}
      style={{
        backgroundImage: backgroundImage
      }}
    >
      {renderImage && (
        <img
          data-testid="venue-image"
          src={imageUrl}
          className={cx({
            [styles['previewImage']]: true,
            [styles['previewImage--loading']]: loading
          })}
          height="400"
          width="600"
          ref={imageNode}
          onLoad={imageLoaded}
        />
      )}
    </div>
  );
}
